import type { DeactivatedStudy, Keys } from '@/models/login';
import {
  DATA_VIEW_FILTER_KEY,
  FIELD_DETAILS_SHOWN_KEY,
  PIPELINE_PAGE_SIZE_KEY,
  PIPELINE_PAGE_SIZE_OVERRIDE_KEY,
  SIDEBAR_COLLAPSED_KEY
} from './constants';
import type { StudyModel } from '@/models';
import { isValidJson } from './generalUtils';

/** The stop-gap browser PIN's keys: one browser-wide, then one per user identifier (ENG-9102). */
const BROWSER_PIN_KEY = /^part11pin(_.+)?$/i;

export class LocalStorageUtils {
  static setStudyDetails = (study: StudyModel) => {
    localStorage.setItem('studyId', study.identifier);
    localStorage.setItem('studyTitle', study.title);
    if (study.flags) {
      localStorage.setItem('studyFlags', JSON.stringify(study.flags));
    }
  };

  static getStudyIdentifier = () => {
    return localStorage.getItem('studyId');
  };

  static getCurrentDataViewFilter = (studyId: string) => {
    const filters = localStorage.getItem(DATA_VIEW_FILTER_KEY);
    if (filters && isValidJson(filters)) {
      const filtersJson = JSON.parse(filters);
      // Ensure the parsed value is a non-null object before accessing a property
      if (typeof filtersJson === 'object' && filtersJson !== null) {
        return filtersJson[studyId];
      }
    }
    return null;
  };

  static setDataViewFilter = (studyId: string, filter: string) => {
    const filters = localStorage.getItem(DATA_VIEW_FILTER_KEY);
    let filtersJson: Record<string, string> = {};
    if (filters && isValidJson(filters)) {
      filtersJson = JSON.parse(filters);
    }
    filtersJson[studyId] = filter;
    localStorage.setItem(DATA_VIEW_FILTER_KEY, JSON.stringify(filtersJson));
  };

  static getSidebarCollapsed = () => {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  };

  static setSidebarCollapsed = (collapsed: boolean) => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  };

  /**
   * The entry header's "Show all details" (ENG-9908). Off when the browser refuses storage, as in a private window, so
   * the details start folded; a refused write keeps the switch for the entry on screen and remembers nothing.
   */
  static getFieldDetailsShown = (): boolean => {
    try {
      return localStorage.getItem(FIELD_DETAILS_SHOWN_KEY) === 'true';
    } catch {
      return false;
    }
  };

  static setFieldDetailsShown = (shown: boolean) => {
    try {
      localStorage.setItem(FIELD_DETAILS_SHOWN_KEY, String(shown));
    } catch {
      // Remembered for nobody, and nothing worth telling the reader about.
    }
  };

  /**
   * A row count read back off a key, or `undefined` where the key holds anything that is not a
   * positive whole number — a missing key, a cleared one, or a hand-edited one. All three read as
   * never set, because none of them is worth a grid that asks the API for `NaN` rows.
   *
   * Both page-size keys read this way and the guard is the whole of either accessor, so it is
   * written once rather than in each.
   */
  private static getRowCount = (key: string) => {
    const stored = Number(localStorage.getItem(key));
    return Number.isInteger(stored) && stored > 0 ? stored : undefined;
  };

  /** `undefined` clears the key rather than writing one, so a cleared value reads as never set. */
  private static setRowCount = (key: string, rows?: number) => {
    if (rows === undefined) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, String(rows));
  };

  /** The page size the pipeline grid measured for itself last time, or `undefined` if it never has. */
  static getPipelinePageSize = () => LocalStorageUtils.getRowCount(PIPELINE_PAGE_SIZE_KEY);

  static setPipelinePageSize = (rows: number) => LocalStorageUtils.setRowCount(PIPELINE_PAGE_SIZE_KEY, rows);

  /**
   * The page size the reader picked for the pipeline grid, or `undefined` if they never have — in
   * which case the measurement above stands.
   */
  static getPipelinePageSizeOverride = () => LocalStorageUtils.getRowCount(PIPELINE_PAGE_SIZE_OVERRIDE_KEY);

  static setPipelinePageSizeOverride = (rows?: number) =>
    LocalStorageUtils.setRowCount(PIPELINE_PAGE_SIZE_OVERRIDE_KEY, rows);

  static logoutUser = () => {
    localStorage.removeItem('keys');
    localStorage.removeItem('studyId');
    localStorage.removeItem('studyTitle');
    localStorage.removeItem('studyFlags');
  };

  static storeUserKeys = (keys: Keys) => {
    localStorage.setItem('keys', JSON.stringify(keys));
  };

  static onLoginSuccess = () => {
    const now = new Date().toISOString();

    const previousLogin = localStorage.getItem('lastLogin');

    if (previousLogin) {
      localStorage.setItem('lastLoginDisplay', previousLogin);
    }

    localStorage.setItem('lastLogin', now);
  };

  static setUserName = (userName: string) => {
    const keys = this.getUserKeys();
    keys.name = userName;
    this.storeUserKeys(keys);
  };

  static setUserAvatar = (newAvatar: string) => {
    const keys = this.getUserKeys();
    keys.avatar = newAvatar;
    this.storeUserKeys(keys);
  };

  static getUserKeys = (): Keys => {
    const keys = localStorage.getItem('keys');
    return keys ? JSON.parse(keys) : null;
  };

  /**
   * The studies the sign-in answer, or `/study`'s later read of it, said the person was deactivated in,
   * when they hold no other (users decision 138); empty when signed out or holding a study.
   */
  static getDeactivatedStudies = (): DeactivatedStudy[] => this.getUserKeys()?.deactivatedStudies ?? [];

  /** Replaces them with a later answer than the sign-in's (ENG-10321); does nothing when signed out. */
  static setDeactivatedStudies = (studies: DeactivatedStudy[]) => {
    const keys = this.getUserKeys();
    if (!keys) return;
    keys.deactivatedStudies = studies;
    this.storeUserKeys(keys);
  };

  static getFirstName = () => {
    const keys = localStorage.getItem('keys');
    const keysJson: Keys = keys ? JSON.parse(keys) : undefined;
    return keysJson && keysJson.name ? keysJson.name.split(' ')[0] : '';
  };

  static getUserName = () => {
    const keys = localStorage.getItem('keys');
    const keysJson: Keys = keys ? JSON.parse(keys) : undefined;
    return keysJson && keysJson.name ? keysJson.name : '';
  };

  static getUserEmail = () => {
    const keys = localStorage.getItem('keys');
    const keysJson: Keys = keys ? JSON.parse(keys) : undefined;
    return keysJson && keysJson.email ? keysJson.email : '';
  };

  static getUserId = () => {
    const keys = localStorage.getItem('keys');
    const keysJson: Keys = keys ? JSON.parse(keys) : undefined;
    return keysJson && keysJson.identifier ? keysJson.identifier : '';
  };

  /**
   * Removes the Part 11 PIN the browser kept before spdata-api held it (ENG-9102's stop-gap, deleted by
   * ENG-9108): the old browser-wide key and the per-user ones, named by a user identifier after an
   * underscore. `main.ts` calls it on load. Matched without regard to case so the old key's spelling
   * need not survive anywhere in `src/`.
   */
  static forgetBrowserPins = () => {
    // Best effort: it runs before the app mounts, and a browser that blocks storage holds no old PIN to drop.
    try {
      Object.keys(localStorage)
        .filter(key => BROWSER_PIN_KEY.test(key))
        .forEach(key => localStorage.removeItem(key));
    } catch {
      // Nothing to clean up, and nothing worth stopping the app over.
    }
  };

  static saveFcmToken = (token: string) => {
    localStorage.setItem('fcmToken', token);
  };

  static getFcmToken = () => {
    return localStorage.getItem('fcmToken');
  };
}
